import type { ClearedPair } from "../components/ClearedSubsequentTab";
import type { FrameRecords, ReconResult } from "../types/api";

// The app's own Cleared in Subsequent Month pairings, which finance can RELEASE (backend
// manual_cleared.withhold_released_clearances, 2026-10-14): engine pairs of these two
// sections. A hand-routed pairing is undone by Unmatch, a settled-before one by the
// prior-period undo. Ported to tests/cleared_release_port.py: keep the two in lockstep.
export const RELEASABLE_SECTIONS = ["in_period", "before_period"];

export interface AutoClearing {
  // The search panel's status chip for the leg.
  status: string;
  // The pairing's other leg, by its _sig ("" when it is not in the frames).
  others: string[];
}

// The _sig of the row at `index` in `frame`, through a position map built once per frame.
function sigLookup(frame: FrameRecords | undefined): (index: number) => string {
  const si = frame ? frame.columns.indexOf("_sig") : -1;
  if (!frame || si < 0) return () => "";
  const pos = new Map<unknown, number>();
  frame.indices.forEach((ix, p) => pos.set(ix, p));
  return (index) => {
    const p = pos.get(index);
    return p === undefined ? "" : String(frame.rows[p][si] ?? "");
  };
}

export function isReleasableClearing(p: ClearedPair): boolean {
  return p.evidence !== "manual" && p.group == null
    && RELEASABLE_SECTIONS.includes(p.section ?? "in_period");
}

// {leg _sig: AutoClearing} for both legs of every releasable pairing.
export function autoClearings(result: ReconResult): Map<string, AutoClearing> {
  const out = new Map<string, AutoClearing>();
  const pairs = (result.cleared_subsequent as ClearedPair[] | undefined) ?? [];
  if (!pairs.length) return out;
  const at = { cri: sigLookup(result.df_cri), insurer: sigLookup(result.df_insurer) };
  for (const p of pairs) {
    if (!isReleasableClearing(p)) continue;
    const open = at[p.in_window_side](p.in_window_index);
    const clear = at[p.clearing_side](p.clearing_index);
    const status = `Cleared in ${p.cleared_in || "a later month"} · by the app`;
    if (open) out.set(open, { status, others: clear ? [clear] : [] });
    if (clear) out.set(clear, { status, others: open ? [open] : [] });
  }
  return out;
}

// {"<side>:<index>" of a releasable pairing's open row: its _sig}, the key the Cleared tab
// releases the pairing by.
export function releaseKeys(result: ReconResult): Map<string, string> {
  const out = new Map<string, string>();
  const pairs = (result.cleared_subsequent as ClearedPair[] | undefined) ?? [];
  if (!pairs.length) return out;
  const at = { cri: sigLookup(result.df_cri), insurer: sigLookup(result.df_insurer) };
  for (const p of pairs) {
    const sig = isReleasableClearing(p) ? at[p.in_window_side](p.in_window_index) : "";
    if (sig) out.set(`${p.in_window_side}:${p.in_window_index}`, sig);
  }
  return out;
}
